import { createElement, useEffect, useRef, useState } from 'react';
import { abrirFluxo, api, token } from './api';
import { Aviso } from './ui';

/**
 * O tempo real na tela — onda 548 (Plano 2 · Comercial, Etapa 1, Tarefa 3).
 *
 * `useTempoReal(topicos, aoReceber)` abre o fluxo SSE da API para os tópicos
 * e chama `aoReceber` a cada aviso. O aviso só traz ids («mudou, vá
 * buscar»): quem recebe busca o resto pela rota de sempre, que confere a
 * permissão de novo.
 *
 * ─── O contrato do «recarregar» ───────────────────────────────────────
 *
 * Quando a tela pode ter perdido avisos, `aoReceber` recebe, em cada tópico,
 * `{ topico, carga: { recarregar: true } }`, e quem consome busca TUDO de
 * novo. Acontece em três casos:
 *  · a API perdeu a escuta do banco e voltou (o servidor manda);
 *  · o fluxo caiu e voltou (o hook manda, ao reabrir);
 *  · enquanto o fluxo está fora, a cada 30 s — é a «consulta» que mantém a
 *    tela andando, e o `AtualizacaoPausada` diz que é isso que está
 *    acontecendo.
 *
 * ─── Por que o bilhete ────────────────────────────────────────────────
 *
 * O `EventSource` não manda cabeçalho, e a sessão não vai na URL. Cada
 * abertura tira um bilhete de 60 s (`POST /tempo-real/bilhete`), que só abre
 * o fluxo e não é sessão (I6). O fluxo caído reabre com bilhete novo: o
 * `EventSource` reconectaria sozinho com o velho, já vencido.
 *
 * ─── O 204 no bilhete: «aqui não há tempo real» ───────────────────────
 *
 * O bilhete que volta 204 (sem corpo) é o servidor dizendo que o tempo real
 * está desligado — é o que o arreio dos testes de tela faz (`servirTela`), e
 * o que um nginx ou a API podem fazer para desligá-lo. A tela fica pausada,
 * consultando a cada 30 s, e não reabre até a pessoa ou os tópicos mudarem
 * (535.16). O 204 no FLUXO, que no SSE quer dizer «não reconecte», o
 * `EventSource` não deixa ver: chega como o 401, o 403 ou o 502, num
 * `onerror` com o fluxo fechado. Por isso o «desligado» se diz no bilhete,
 * e a queda do fluxo continua sendo queda — reabre com bilhete novo.
 */

export interface EventoTempoReal {
  topico: string;
  carga: Record<string, unknown>;
}

export interface EstadoDoTempoReal {
  /** O fluxo está aberto e os avisos chegam na hora. */
  conectado: boolean;
  /** O fluxo caiu (ou não abriu, ou o tópico foi recusado): a tela consulta a cada 30 s. */
  pausado: boolean;
}

/** As esperas para reabrir: 1, 2, 5, 10 e, daí em diante, 30 s. */
const ESPERAS_MS = [1000, 2000, 5000, 10000, 30000];
const CONSULTA_MS = 30_000;

/*
 * ─── Um fluxo por aba (onda 550, Tarefa 14) ───────────────────────────
 *
 * O sino mora em toda tela e a caixa de entrada abre a dela: um fluxo por
 * `useTempoReal` daria dois (ou três, com a conversa) por aba, e o teto é de
 * 10 por pessoa (Tarefa 3) — quem trabalha com muitas abas cairia na
 * consulta a cada 30 s na sexta aba. Por isso a aba tem UM gerenciador: ele
 * soma os tópicos de todos os `useTempoReal` montados, abre um fluxo só com
 * a soma e reabre quando ela muda (no mesmo instante em que as telas montam
 * e desmontam: as mudanças da mesma passada viram uma reabertura só). Cada
 * aviso vai a quem assinou o tópico dele.
 *
 * O tópico recusado não derruba o fluxo dos outros: o bilhete recebe os
 * tópicos e diz quais valem (`aceitos`) e quais não (`recusados`), e o fluxo
 * abre só com os aceitos. A tela que pediu um recusado fica pausada (a
 * consulta de 30 s, e o aviso «Atualização automática pausada»), e o
 * gerenciador pergunta de novo de tempos em tempos (1, 2, 5, 10, 30 s…) —
 * o acesso pode ter sido dado. Sem nenhum aceito, é como a queda: o fluxo
 * não abre, e o bilhete novo vem na espera seguinte.
 *
 * O «recarregar» continua o mesmo contrato, por tela: toda tela que passa a
 * receber pelo fluxo (ao abrir, ao reabrir depois de uma queda ou de uma
 * troca da soma, ou quando o tópico dela passa a ser aceito) recebe
 * `{ recarregar: true }` nos tópicos dela — entre a busca da tela e o fluxo,
 * ou entre o fluxo velho e o novo, um aviso pode ter passado.
 *
 * ─── A queda e a consulta (rodada de correção 1) ──────────────────────
 *
 * «Em queda» é um estado próprio (`emQueda`): liga quando o fluxo cai (ou
 * nada foi aceito, ou o bilhete diz 204) e só desliga quando um fluxo abre
 * de verdade (`onopen`). Enquanto ele vale, as telas estão pausadas e a
 * consulta de 30 s roda — também durante as tentativas de reabrir, que
 * passam por «abrindo» (a primeira versão desligava a consulta ali, e uma
 * queda de 5 min não entregava nenhum «recarregar»).
 *
 * As esperas são uma escada por motivo: a da queda (zera quando o fluxo
 * abre) e a da reconferência dos tópicos recusados. Uma queda depois de
 * horas conectada recomeça em 1 s.
 */

interface Assinatura {
  topicos: string[];
  ouvir: { current: (ev: EventoTempoReal) => void };
  /** Redesenha a tela que assinou (o estado dela mudou). */
  avisar: () => void;
  /** Recebe pelo fluxo agora (para saber quando ela PASSA a receber). */
  recebendo: boolean;
}

type EstadoDoFluxo = 'parado' | 'abrindo' | 'conectado' | 'desligado';

interface RespostaDoBilhete { bilhete?: string; aceitos?: string[]; recusados?: string[] }

const fluxo = {
  assinaturas: new Set<Assinatura>(),
  /** A soma dos tópicos e quem está na sessão, do fluxo aberto (ou abrindo). */
  pedido: '',
  uniao: [] as string[],
  fonte: null as EventSource | null,
  estado: 'parado' as EstadoDoFluxo,
  /** Caiu (ou não abriu) e ainda não abriu de novo: as telas consultam a cada 30 s. */
  emQueda: false,
  aceitos: new Set<string>(),
  recusados: new Set<string>(),
  /** A escada da queda e a da reconferência dos recusados, cada uma a sua. */
  tentativaQueda: 0,
  tentativaRecusa: 0,
  espera: null as number | null,
  consulta: null as number | null,
  agendado: null as number | null,
  /** Cada abertura (e cada queda) tem um número: o que é de antes não mexe em nada. */
  geracao: 0,
};

const conectadaAgora = (a: Assinatura) => fluxo.estado === 'conectado'
  && a.topicos.every((t) => fluxo.aceitos.has(t));

function estadoDe(a: Assinatura): EstadoDoTempoReal {
  if (conectadaAgora(a)) return { conectado: true, pausado: false };
  const pausado = fluxo.emQueda || fluxo.estado === 'desligado'
    || a.topicos.some((t) => fluxo.recusados.has(t));
  return { conectado: false, pausado };
}

const recarregar = (a: Assinatura) => {
  for (const topico of a.topicos) a.ouvir.current({ topico, carga: { recarregar: true } });
};

/** A consulta de 30 s: só para quem está pausado. Liga e desliga sozinha. */
function acertarConsulta() {
  const alguemPausado = [...fluxo.assinaturas].some((a) => estadoDe(a).pausado);
  if (alguemPausado && fluxo.consulta === null) {
    fluxo.consulta = window.setInterval(() => {
      for (const a of fluxo.assinaturas) if (estadoDe(a).pausado) recarregar(a);
    }, CONSULTA_MS);
  } else if (!alguemPausado && fluxo.consulta !== null) {
    window.clearInterval(fluxo.consulta);
    fluxo.consulta = null;
  }
}

function fecharFonte() {
  fluxo.fonte?.close();
  fluxo.fonte = null;
}

function pararEspera() {
  if (fluxo.espera !== null) window.clearTimeout(fluxo.espera);
  fluxo.espera = null;
}

/*
 * O estado mudou: quem passou a receber pelo fluxo recebe o «recarregar»
 * (M-1: também quem estava pausado ou nunca tinha recebido), a consulta se
 * acerta e as telas redesenham.
 */
function mudou() {
  for (const a of fluxo.assinaturas) {
    const agora = conectadaAgora(a);
    if (agora && !a.recebendo) recarregar(a);
    a.recebendo = agora;
  }
  acertarConsulta();
  for (const a of fluxo.assinaturas) a.avisar();
}

/** A próxima espera de uma escada (1, 2, 5, 10, 30 s…). */
function proximaEspera(escada: 'tentativaQueda' | 'tentativaRecusa'): number {
  const ms = ESPERAS_MS[Math.min(fluxo[escada], ESPERAS_MS.length - 1)];
  fluxo[escada]++;
  return ms;
}

/** Caiu (ou nada foi aceito): em queda, consulta, e pede bilhete novo na espera. */
function cair(g: number) {
  if (g !== fluxo.geracao) return;
  /* Uma geração nova: o que estava em voo (a reconferência, o fluxo velho) não mexe mais. */
  fluxo.geracao++;
  fecharFonte();
  pararEspera();
  fluxo.estado = 'parado';
  fluxo.emQueda = true;
  fluxo.espera = window.setTimeout(() => { fluxo.espera = null; void abrir(); }, proximaEspera('tentativaQueda'));
  mudou();
}

/*
 * O servidor disse que não há tempo real aqui (204 no bilhete): as telas
 * ficam pausadas, consultando a cada 30 s, e NÃO se reabre — pedir bilhete
 * em laço só faria barulho. Pergunta de novo quando a soma dos tópicos ou a
 * pessoa mudam, ou a página recarrega.
 */
function desligar(g: number) {
  if (g !== fluxo.geracao) return;
  fluxo.geracao++;
  fecharFonte();
  pararEspera();
  fluxo.estado = 'desligado';
  fluxo.emQueda = true;
  mudou();
}

async function pedirBilhete(uniao: string[]): Promise<RespostaDoBilhete | undefined> {
  return api<RespostaDoBilhete | undefined>('/tempo-real/bilhete', { metodo: 'POST', corpo: { topicos: uniao } });
}

/** Abre o fluxo com os tópicos aceitos do bilhete. */
function conectar(g: number, r: RespostaDoBilhete) {
  const uniao = fluxo.uniao;
  fluxo.aceitos = new Set((r.aceitos ?? uniao).filter((t) => uniao.includes(t)));
  fluxo.recusados = new Set(r.recusados ?? []);
  if (fluxo.aceitos.size === 0) { cair(g); return; }
  const q = new URLSearchParams({ bilhete: r.bilhete!, topicos: [...fluxo.aceitos].join(',') });
  const f = abrirFluxo(`/tempo-real?${q}`);
  fluxo.fonte = f;
  f.onopen = () => {
    if (g !== fluxo.geracao || fluxo.fonte !== f) return;
    fluxo.tentativaQueda = 0;
    fluxo.emQueda = false;
    fluxo.estado = 'conectado';
    /* Algum tópico recusado: pergunta de novo de tempos em tempos. */
    pararEspera();
    if (fluxo.recusados.size > 0) agendarReconferencia(g);
    mudou();
  };
  f.onmessage = (m: MessageEvent<string>) => {
    if (g !== fluxo.geracao) return;
    let ev: { topico?: unknown; carga?: unknown };
    try { ev = JSON.parse(m.data); } catch { return; }
    if (typeof ev.topico !== 'string') return;
    const topico = ev.topico;
    const carga = (typeof ev.carga === 'object' && ev.carga !== null ? ev.carga : {}) as Record<string, unknown>;
    for (const a of fluxo.assinaturas) if (a.topicos.includes(topico)) a.ouvir.current({ topico, carga });
  };
  f.onerror = () => { if (fluxo.fonte === f) cair(g); };
}

/** Com o fluxo aberto e algum tópico recusado: o acesso pode ter sido dado. */
function agendarReconferencia(g: number) {
  fluxo.espera = window.setTimeout(async () => {
    fluxo.espera = null;
    if (g !== fluxo.geracao) return;
    try {
      const r = await pedirBilhete(fluxo.uniao);
      if (g !== fluxo.geracao) return;
      const aceitos = (r?.aceitos ?? []).filter((t) => fluxo.uniao.includes(t));
      if (r?.bilhete && aceitos.some((t) => !fluxo.aceitos.has(t))) {
        /* Um tópico passou a valer: o fluxo novo leva ele (o bilhete é deste pedido). */
        const nova = ++fluxo.geracao;
        fecharFonte();
        fluxo.estado = 'abrindo';
        fluxo.tentativaRecusa = 0;
        mudou();
        conectar(nova, r);
        return;
      }
    } catch { /* sem rede: a próxima espera tenta de novo */ }
    if (g === fluxo.geracao && fluxo.espera === null) agendarReconferencia(g);
  }, proximaEspera('tentativaRecusa'));
}

async function abrir() {
  const g = ++fluxo.geracao;
  fecharFonte();
  pararEspera();
  /* «Abrindo» não tira ninguém da queda: a consulta continua até o fluxo abrir. */
  fluxo.estado = 'abrindo';
  mudou();
  // Sem sessão não há bilhete; a tela de entrada cuida do resto.
  if (!token.ler()) { cair(g); return; }
  try {
    const r = await pedirBilhete(fluxo.uniao);
    if (g !== fluxo.geracao) return;
    if (!r?.bilhete) { desligar(g); return; }
    conectar(g, r);
  } catch {
    cair(g);
  }
}

/** A soma dos tópicos mudou (ou a pessoa): reabre uma vez, na mesma passada. */
function reconciliar() {
  fluxo.agendado = null;
  const uniao = [...new Set([...fluxo.assinaturas].flatMap((a) => a.topicos))].sort();
  const pedido = uniao.length ? `${uniao.join(',')}|${token.ler() ?? ''}` : '';
  if (pedido === fluxo.pedido) { mudou(); return; }
  fluxo.pedido = pedido;
  fluxo.uniao = uniao;
  fluxo.tentativaQueda = 0;
  fluxo.tentativaRecusa = 0;
  fluxo.recusados = new Set();
  if (!uniao.length) {
    fluxo.geracao++;
    fecharFonte();
    pararEspera();
    fluxo.estado = 'parado';
    fluxo.emQueda = false;
    fluxo.aceitos = new Set();
    mudou();
    return;
  }
  void abrir();
}

function agendar() {
  if (fluxo.agendado === null) fluxo.agendado = window.setTimeout(reconciliar, 0);
}

export function useTempoReal(
  topicos: string[],
  aoReceber: (ev: EventoTempoReal) => void,
): EstadoDoTempoReal {
  const [, setVez] = useState(0);
  const ouvinte = useRef(aoReceber);
  useEffect(() => { ouvinte.current = aoReceber; }, [aoReceber]);
  const assinatura = useRef<Assinatura | null>(null);

  // A lista vira uma chave estável: o mesmo conjunto de tópicos não reabre o fluxo.
  const chave = [...new Set(topicos.filter(Boolean))].sort().join(',');
  /*
   * E a pessoa entra na chave (M3): no «Entrar como», na saída e numa nova
   * entrada, o token muda e o fluxo reabre com o bilhete de quem está agora.
   * Sem isso, o fluxo aberto em nome da pessoa anterior continuaria
   * entregando os avisos dela na tela da seguinte.
   */
  const quem = token.ler() ?? '';

  useEffect(() => {
    if (!chave) { assinatura.current = null; setVez((n) => n + 1); return undefined; }
    const a: Assinatura = {
      topicos: chave.split(','),
      ouvir: ouvinte,
      avisar: () => setVez((n) => n + 1),
      recebendo: false,
    };
    assinatura.current = a;
    fluxo.assinaturas.add(a);
    agendar();
    return () => {
      fluxo.assinaturas.delete(a);
      if (assinatura.current === a) assinatura.current = null;
      agendar();
    };
  }, [chave, quem]);

  const a = assinatura.current;
  if (!a) return { conectado: false, pausado: false };
  return estadoDe(a);
}

/**
 * «Atualização automática pausada»: o aviso que a tela ao vivo mostra
 * enquanto o fluxo está fora. Some sozinho quando o fluxo volta.
 */
export function AtualizacaoPausada({ estado }: { estado: EstadoDoTempoReal }) {
  if (!estado.pausado) return null;
  return createElement(Aviso, { tipo: 'info', titulo: 'Atualização automática pausada' },
    'A tela confere de novo a cada 30 segundos enquanto a conexão não volta.');
}
